import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import {
  Menu,
  MenuGroupHeading,
  MenuItem,
  MenuProps,
} from '@lifeomic/chroma-react/components/Menu';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  Apple,
  BoneJoint,
  Brain,
  CapsulePills,
  CheckCircle,
  CheckList,
  ChevronDown,
  File,
  Globe,
  Heart,
  Hexagons2,
  List,
  Ribbon,
  Sparkle,
  SurvivalCurve,
  TestTube,
} from '@lifeomic/chromicons';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Text } from '@lifeomic/chroma-react/components/Text';
import { KnownImpressionTypes } from '../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';

const messages = defineMessages({
  name: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.name',
    defaultMessage: 'Impression Type Filter',
  },
  summaryGroupHeader: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.summaryGroupHeader',
    defaultMessage: 'Section',
  },
  menu: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.menu',
    defaultMessage: 'Impression Type Filter Menu',
  },
  actionItems: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.actionItems',
    defaultMessage: 'Action Plan',
  },
  'functional-biomarker-profile': {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.functionalBiomarkerProfile',
    defaultMessage: 'Functional Biomarker Profile',
  },
  labResults: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.labResults',
    defaultMessage: 'Lab Results',
  },
  cancer: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.cancer',
    defaultMessage: 'Cancer Risk',
  },
  sectionTypeLabel: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.sectionTypeLabel',
    defaultMessage: 'Section Type',
  },
  impressionTypeCaption: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.impressionTypeCaption',
    defaultMessage:
      'Used for showing an appropriate icon and heading when presenting and printing the report.',
  },
  all: {
    id: 'phc-web-toolkit.aiTemplates.impressionTypeFilter.all',
    defaultMessage: 'All',
  },
});

const useStyles = makeStyles(theme => ({
  textLabel: {
    textTransform: 'capitalize',
    flexGrow: 1,
  },
  formFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  formFieldMenu: {
    // these styles make the Menu look like a Select control.
    // it uses a Menu because we want the icon-decorated text and options
    backgroundColor: theme.hexToRgba(theme.palette.graphite[900], 0.15),
    '&:hover': {
      backgroundColor: theme.hexToRgba(theme.palette.graphite[900], 0.15),
    },
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    '& :nth-child(1)': {},
  },
  impressionTypeLabel: {
    fontSize: theme.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
  },
  caption: {
    ...theme.typography.caption,
    paddingLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

export type Sections = KnownImpressionTypes | KnownExtraSections;

export type KnownExtraSections = 'actionItems' | 'labResults' | 'all';

type ImpressionTypeFilterProps = {
  impressionTypes: Sections[];
  onChange: (value: Sections) => void;
  value: Sections;
  disabled: boolean;
  extraSections?: KnownExtraSections[];
  variant?: 'menu' | 'form-field';
  impressionTypeCaption?: string;
};

type ButtonIcon = Parameters<typeof Button>[0]['icon'];

export const ICON_MAP: Record<
  KnownImpressionTypes | KnownExtraSections,
  ButtonIcon
> = {
  all: CheckCircle,
  actionItems: CheckList,
  labResults: TestTube,
  brain: Brain,
  cancer: Ribbon,
  heart: Heart,
  metabolic: Hexagons2,
  musculoskeletal: BoneJoint,
  nutrition: Apple,
  overview: Globe,
  synopsis: List,
  supplements: CapsulePills,
  'functional-biomarker-profile': SurvivalCurve,
  general: File,
};

export const ImpressionTypeFilter = (props: ImpressionTypeFilterProps) => {
  const {
    onChange,
    value,
    impressionTypes,
    extraSections = ['actionItems', 'labResults'],
    variant = 'menu',
  } = props;
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const menuItems: MenuProps['items'] = impressionTypes.map(impressionType => (
    <MenuItem
      icon={ICON_MAP[impressionType] || Sparkle}
      className={classes.textLabel}
      key={impressionType}
      data-testid={`ai-templates-filter-${impressionType}`}
      disabled={props.disabled}
      onClick={() => {
        onChange(impressionType);
        return true;
      }}
    >
      {impressionType in messages
        ? formatMessage(messages[impressionType as keyof typeof messages])
        : impressionType}
    </MenuItem>
  ));

  return (
    <div
      className={
        variant === 'form-field' ? classes.formFieldContainer : undefined
      }
    >
      {variant === 'form-field' && (
        <Text className={classes.impressionTypeLabel}>
          {formatMessage(messages.sectionTypeLabel)}
        </Text>
      )}
      <Menu
        placement="bottom-start"
        aria-label={formatMessage(messages.menu)}
        anchorElement={
          <Button
            variant={'text'}
            className={
              variant === 'form-field' ? classes.formFieldMenu : undefined
            }
            icon={ICON_MAP[value as KnownImpressionTypes] || Sparkle}
            trailingIcon={ChevronDown}
            aria-label={formatMessage(messages.name)}
          >
            <span className={classes.textLabel}>
              {value in messages
                ? formatMessage(messages[value as keyof typeof messages])
                : value}
            </span>
          </Button>
        }
        data-testid="ai-templates-impression-type-filter"
        items={[
          ...(variant === 'menu'
            ? [
                <MenuGroupHeading key="summaryGroupHeader">
                  {formatMessage(messages.summaryGroupHeader)}
                </MenuGroupHeading>,
              ]
            : []),
          ...menuItems,
          ...(extraSections.length > 0 ? [<Divider key="divider" />] : []),
          ...(extraSections.includes('labResults')
            ? [
                <MenuItem
                  key="labResults"
                  icon={ICON_MAP.labResults}
                  className={classes.textLabel}
                  data-testid={`es-filter-labResults`}
                  disabled={props.disabled}
                  onClick={() => {
                    onChange('labResults');
                  }}
                >
                  {formatMessage(messages.labResults)}
                </MenuItem>,
              ]
            : []),
          ...(extraSections.includes('actionItems')
            ? [
                <MenuItem
                  key="actionItems"
                  icon={ICON_MAP.actionItems}
                  className={classes.textLabel}
                  data-testid={`es-filter-actionItems`}
                  disabled={props.disabled}
                  onClick={() => {
                    onChange('actionItems');
                  }}
                >
                  {formatMessage(messages.actionItems)}
                </MenuItem>,
              ]
            : []),
          ...(extraSections.includes('all')
            ? [
                <MenuItem
                  key="all"
                  icon={ICON_MAP.all}
                  className={classes.textLabel}
                  data-testid={`es-filter-all`}
                  disabled={props.disabled}
                  onClick={() => {
                    onChange('all');
                  }}
                >
                  {formatMessage(messages.all)}
                </MenuItem>,
              ]
            : []),
        ]}
      />
      {variant === 'form-field' && (
        <Text className={classes.caption}>
          {props.impressionTypeCaption ||
            formatMessage(messages.impressionTypeCaption)}
        </Text>
      )}
    </div>
  );
};
